import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { FormStore, FormStep, IdentityDocumentsStatus, CoperturaFornitura } from '@/types'
import { defaultFormData, getFormSteps, SIGNING_STEPS } from '@/types'

function getAllSteps(mode: 'self' | 'operator'): FormStep[] {
  return [...getFormSteps(mode), ...SIGNING_STEPS]
}

function scrollWizardToTop(): void {
  if (typeof window === 'undefined') return
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
}

export const useFormStore = create<FormStore>()(
  persist(
  (set, get) => ({
      data: { ...defaultFormData },
      // Step iniziale del wizard. Lo step 'upload' (caricamento bolletta +
      // OCR) e' stato rimosso da maggio 2026: per la modalita' self-service
      // il primo step e' la scelta tipo cliente, per la modalita' operatore
      // FormWrapper rimappera' a 'anagrafica'.
      step: 'tipo_cliente',
      submissionId: null,
      ocrAssisted: false,
      isDirty: false,
      mode: 'self',
      
      // Signing state
      signingSessionId: null,
      signingSession: null,
      signatures: [],
      generatedDocuments: [],
      
      // Identity documents state
      identityDocumentsStatus: null,
      
      // Copertura territoriale
      coperturaFornitura: null,

      setData: (newData) => {
        set((state) => {
          const updatedData = { ...state.data, ...newData }
          return {
            data: updatedData,
            isDirty: true
          }
        })
      },

      setStep: (step) => {
        scrollWizardToTop()
        set({ step })
      },

      setSubmissionId: (id) => {
        set({ submissionId: id })
      },

      setOcrAssisted: (value) => {
        set({ ocrAssisted: value })
      },

      setMode: (mode) => {
        set({ mode })
      },

      setSigningSessionId: (id) => {
        set({ signingSessionId: id })
      },

      setSigningSession: (session) => {
        set({ signingSession: session })
      },

      addSignature: (signature) => {
        set((state) => {
          // Evita duplicati
          const exists = state.signatures.some(s => s.fieldId === signature.fieldId)
          if (exists) {
            return state
          }
          return {
            signatures: [...state.signatures, signature]
          }
        })
      },

      setSignatures: (signatures) => {
        set({ signatures })
      },

      setGeneratedDocuments: (docs) => {
        set({ generatedDocuments: docs })
      },

      setIdentityDocumentsStatus: (status: IdentityDocumentsStatus | null) => {
        set({ identityDocumentsStatus: status })
      },

      setCoperturaFornitura: (copertura: CoperturaFornitura | null) => {
        set({ coperturaFornitura: copertura })
      },

      resetForm: () => {
        scrollWizardToTop()
        set({
          data: { ...defaultFormData },
          step: 'tipo_cliente',
          submissionId: null,
          ocrAssisted: false,
          isDirty: false,
          mode: 'self',
          signingSessionId: null,
          signingSession: null,
          signatures: [],
          generatedDocuments: [],
          identityDocumentsStatus: null,
          coperturaFornitura: null
        })
      },

      nextStep: () => {
        const { step, mode } = get()
        const steps = getAllSteps(mode)
        const currentIndex = steps.indexOf(step)
        if (currentIndex < steps.length - 1) {
          scrollWizardToTop()
          set({ step: steps[currentIndex + 1] })
        }
      },

      prevStep: () => {
        const { step, mode } = get()
        const steps = getAllSteps(mode)
        const currentIndex = steps.indexOf(step)
        if (currentIndex > 0) {
          scrollWizardToTop()
          set({ step: steps[currentIndex - 1] })
        }
      }
    }),
    {
      name: 'heracomm-form',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        data: state.data,
        step: state.step,
        ocrAssisted: state.ocrAssisted,
        mode: state.mode,
        coperturaFornitura: state.coperturaFornitura,
      }),
    }
  )
)

// Helper hooks
export const useFormData = () => useFormStore((state) => state.data)
export const useFormStep = () => useFormStore((state) => state.step)
export const useOcrAssisted = () => useFormStore((state) => state.ocrAssisted)
export const useSigningSession = () => useFormStore((state) => state.signingSession)
export const useSignatures = () => useFormStore((state) => state.signatures)
export const useGeneratedDocuments = () => useFormStore((state) => state.generatedDocuments)
export const useIdentityDocumentsStatus = () => useFormStore((state) => state.identityDocumentsStatus)
export const useCoperturaFornitura = () => useFormStore((state) => state.coperturaFornitura)
export const useFormMode = () => useFormStore((state) => state.mode)
export const useFormActions = () => useFormStore((state) => ({
  setData: state.setData,
  setStep: state.setStep,
  setSubmissionId: state.setSubmissionId,
  setOcrAssisted: state.setOcrAssisted,
  setMode: state.setMode,
  setSigningSessionId: state.setSigningSessionId,
  setSigningSession: state.setSigningSession,
  addSignature: state.addSignature,
  setSignatures: state.setSignatures,
  setGeneratedDocuments: state.setGeneratedDocuments,
  setIdentityDocumentsStatus: state.setIdentityDocumentsStatus,
  setCoperturaFornitura: state.setCoperturaFornitura,
  resetForm: state.resetForm,
  nextStep: state.nextStep,
  prevStep: state.prevStep
}))

